import { create } from 'zustand';
import { apiGet, apiSend, setAccessToken } from '../lib/api';
import type { AuthUser } from '../lib/types';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  status: 'idle' | 'loading' | 'authed' | 'anon';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  setLost: () => void;
}

const LS_KEY = 'jx_admin_token';

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  token: typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null,
  status: 'idle',

  async login(email, password) {
    set({ status: 'loading' });
    const res = await apiSend<{ user: AuthUser; accessToken: string }>('post', '/auth/login', {
      email,
      password,
    });
    setAccessToken(res.accessToken);
    localStorage.setItem(LS_KEY, res.accessToken);
    set({ user: res.user, token: res.accessToken, status: 'authed' });
  },

  async logout() {
    try {
      await apiSend('post', '/auth/logout');
    } catch {
      /* ignore */
    }
    setAccessToken(null);
    localStorage.removeItem(LS_KEY);
    set({ user: null, token: null, status: 'anon' });
  },

  async bootstrap() {
    const token = get().token || localStorage.getItem(LS_KEY);
    if (token) setAccessToken(token);
    set({ status: 'loading' });
    try {
      const user = await apiGet<AuthUser>('/auth/me');
      set({ user, status: 'authed', token: token ?? null });
    } catch {
      // try a silent refresh via cookie
      try {
        const res = await apiSend<{ user: AuthUser; accessToken: string }>('post', '/auth/refresh');
        setAccessToken(res.accessToken);
        localStorage.setItem(LS_KEY, res.accessToken);
        set({ user: res.user, token: res.accessToken, status: 'authed' });
      } catch {
        setAccessToken(null);
        localStorage.removeItem(LS_KEY);
        set({ user: null, token: null, status: 'anon' });
      }
    }
  },

  setLost() {
    setAccessToken(null);
    localStorage.removeItem(LS_KEY);
    set({ user: null, token: null, status: 'anon' });
  },
}));
